/**
 * FilterChips — domain and LoR range filter chips for the Scene Tree.
 *
 * P3 — INIT-2026-Q3-SCENE-HIERARCHY (viewer tree completion).
 * Generates chips from the unique domains and LoR levels in the loaded manifest.
 * Active/inactive toggle composes with the text search in SceneTreeSearchBar.
 */

import { useMemo } from "react";
import { tokens } from "@/design";
import type { SceneHierarchyManifest, TreeNodeDomain } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import { DOMAIN_TOKEN_MAP } from "./domainTokenMap";

export interface ActiveFilters {
  readonly domains: ReadonlySet<TreeNodeDomain>;
  readonly lorLevels: ReadonlySet<number>;
}

export interface FilterChipsProps {
  readonly manifest: SceneHierarchyManifest | null;
  readonly activeFilters: ActiveFilters;
  readonly onFiltersChange: (filters: ActiveFilters) => void;
}

export function FilterChips({
  manifest,
  activeFilters,
  onFiltersChange,
}: FilterChipsProps) {
  const { domains, lorLevels } = useMemo(() => {
    if (!manifest) return { domains: [] as TreeNodeDomain[], lorLevels: [] as number[] };

    const domainSet = new Set<TreeNodeDomain>();
    const lorSet = new Set<number>();

    for (const node of manifest.nodes) {
      domainSet.add(node.domain);
      lorSet.add(node.lor);
    }

    return {
      domains: [...domainSet].sort(),
      lorLevels: [...lorSet].sort((a, b) => a - b),
    };
  }, [manifest]);

  if (domains.length === 0 && lorLevels.length === 0) return null;

  function toggleDomain(domain: TreeNodeDomain) {
    const next = new Set(activeFilters.domains);
    if (next.has(domain)) next.delete(domain);
    else next.add(domain);
    onFiltersChange({ ...activeFilters, domains: next });
  }

  function toggleLoR(lor: number) {
    const next = new Set(activeFilters.lorLevels);
    if (next.has(lor)) next.delete(lor);
    else next.add(lor);
    onFiltersChange({ ...activeFilters, lorLevels: next });
  }

  return (
    <div
      data-testid="scene-tree-filter-chips"
      role="group"
      aria-label="Tree filters"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px ${tokens.space[2] - tokens.border.width.thin * 2}px`,
        borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        backgroundColor: tokens.color.surface[1],
        flexShrink: 0,
      }}
    >
      {domains.map((domain) => {
        const isActive = activeFilters.domains.has(domain);
        const entry = DOMAIN_TOKEN_MAP[domain];
        return (
          <button
            key={`domain-${domain}`}
            role="checkbox"
            aria-checked={isActive}
            aria-label={`Filter by ${entry?.ariaLabel ?? domain}`}
            data-testid={`filter-domain-${domain}`}
            onClick={() => toggleDomain(domain)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: tokens.density.rowDense - tokens.space[2] + tokens.border.width.thin * 2,
              padding: `0 ${tokens.space[2]}px`,
              border: isActive
                ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.chip,
              cursor: "pointer",
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.xs,
              fontWeight: isActive ? tokens.font.weight.semibold : tokens.font.weight.regular,
              color: isActive ? tokens.color.accent.primary : tokens.color.ink.secondary,
              backgroundColor: isActive
                ? tokens.color.accent.subtle
                : tokens.color.surface[1],
              whiteSpace: "nowrap",
            }}
          >
            {entry?.label ?? domain}
          </button>
        );
      })}

      {lorLevels.map((lor) => {
        const isActive = activeFilters.lorLevels.has(lor);
        return (
          <button
            key={`lor-${lor}`}
            role="checkbox"
            aria-checked={isActive}
            aria-label={`Filter by LoR ${lor}`}
            data-testid={`filter-lor-${lor}`}
            onClick={() => toggleLoR(lor)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: tokens.density.rowDense - tokens.space[2] + tokens.border.width.thin * 2,
              padding: `0 ${tokens.space[2]}px`,
              border: isActive
                ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.chip,
              cursor: "pointer",
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.xs,
              fontWeight: isActive ? tokens.font.weight.semibold : tokens.font.weight.regular,
              color: isActive ? tokens.color.accent.primary : tokens.color.ink.secondary,
              backgroundColor: isActive ? tokens.color.accent.subtle : tokens.color.surface[1],
              whiteSpace: "nowrap",
            }}
          >
            LoR{lor}
          </button>
        );
      })}
    </div>
  );
}
